import React, { useRef, useEffect } from "react";
import { ChevronDown, ChevronUp, ChevronsUpDown } from "lucide-react";

export interface Column<T> {
  id: keyof T | string;
  header: React.ReactNode;
  cell: (item: T) => React.ReactNode;
  sortable?: boolean;
  minWidth?: number | string;
}

export interface SortConfig<T> {
  column: keyof T | null;
  direction: 'asc' | 'desc';
}

interface ScrollableTableProps<T> {
  data: T[];
  columns: Column<T>[];
  sortConfig?: SortConfig<T>;
  onSort?: (column: keyof T) => void;
  emptyMessage?: string;
  height?: string;
}

export function ScrollableTable<T>({
  data,
  columns,
  sortConfig,
  onSort,
  emptyMessage = "No data found",
  height = "calc(100vh - 370px)"
}: ScrollableTableProps<T>) {
  const headerRef = useRef<HTMLDivElement>(null);
  const bodyRef = useRef<HTMLDivElement>(null);

  // Sync horizontal scroll between header and body
  useEffect(() => {
    const handleBodyScroll = () => {
      if (headerRef.current && bodyRef.current) {
        headerRef.current.scrollLeft = bodyRef.current.scrollLeft;
      }
    };
    
    const bodyElement = bodyRef.current;
    if (bodyElement) {
      bodyElement.addEventListener('scroll', handleBodyScroll);
      return () => {
        bodyElement.removeEventListener('scroll', handleBodyScroll);
      };
    }
  }, []);

  const getSortIcon = (columnId: keyof T | string) => {
    if (sortConfig && sortConfig.column === columnId) {
      return sortConfig.direction === 'asc' ? 
        <ChevronUp className="h-4 w-4 ml-1" /> : 
        <ChevronDown className="h-4 w-4 ml-1" />;
    }
    return <ChevronsUpDown className="h-4 w-4 ml-1 opacity-50" />;
  };

  return (
    <div className="flex flex-col h-full border rounded-md" style={{ height }}>
      {/* Header container - scrolls horizontally but fixed vertically */}
      <div 
        ref={headerRef}
        className="overflow-hidden"
        style={{ width: '100%' }}
      >
        <table className="w-auto min-w-full table-auto">
          <thead>
            <tr>
              {columns.map((column) => (
                <th
                  key={column.id.toString()}
                  className={`bg-background px-4 py-2 border-b text-left ${
                    column.sortable ? 'cursor-pointer hover:bg-muted/50 transition-colors' : ''
                  }`}
                  style={{ minWidth: column.minWidth }}
                  onClick={() => {
                    if (column.sortable && onSort) {
                      onSort(column.id as keyof T);
                    }
                  }}
                >
                  <div className="flex items-center">
                    {column.header}
                    {column.sortable && getSortIcon(column.id)}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
        </table>
      </div>
      
      {/* Body container - scrolls both horizontally and vertically */}
      <div 
        ref={bodyRef}
        className="overflow-auto flex-1"
      >
        <table className="w-auto min-w-full table-auto">
          <tbody>
            {data.length > 0 ? (
              data.map((item, rowIndex) => (
                <tr key={rowIndex}>
                  {columns.map((column) => (
                    <td
                      key={`${rowIndex}-${column.id.toString()}`}
                      className="border-b px-4 py-2"
                      style={{ minWidth: column.minWidth }}
                    >
                      {column.cell(item)}
                    </td>
                  ))}
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={columns.length} className="text-center text-muted-foreground h-24 p-4">
                  {emptyMessage}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
